<template>
  <div class="comment-container">
    <div v-if="loading" class="loading-wrapper">
      <van-loading type="spinner" />
    </div>
    <div v-else>
      <div v-if="isDCFlow" class="comment-group">
        <div class="group-title">Comment:</div>
        <div class="other-input">
          <textarea v-model="dcComment" class="other-textarea" placeholder="please input" />
        </div>
      </div>
      <template v-else>
        <van-empty v-if="groupLabels.length === 0" description="No Data" />
        <div v-else>
          <div v-for="label in groupLabels" :key="label" class="comment-group">
            <div class="group-title">
              <span v-if="label === 'Current Gate'" class="required-star">*</span>{{ label }}:
            </div>
            <div class="group-list">
              <van-radio-group v-if="label === 'Current Gate'" v-model="currentGate">
                <van-radio
                  v-for="item in groupedRefComments[label]"
                  :key="item.id ?? item.typeName"
                  :name="getItemName(item, label)"
                  class="mb-2"
                >
                  {{ item.typeName }}
                </van-radio>
              </van-radio-group>
              <van-checkbox-group v-else v-model="checked[label]">
                <van-checkbox
                  v-for="item in groupedRefComments[label]"
                  :key="item.id ?? item.typeName"
                  :name="getItemName(item, label)"
                  class="mb-2"
                  shape="square"
                >
                  {{ item.typeName }}
                </van-checkbox>
              </van-checkbox-group>
            </div>
            <div
              v-if="groupedRefComments[label]?.some((i: any) => isOtherReasons(i?.typeName))"
              class="other-input"
            >
              <textarea
                v-model="otherReasonsText[label]"
                class="other-textarea"
                placeholder="please input"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
  <div class="fixed-footer">
    <van-button class="save-btn" color="#6E010F" @click="onSave">Save</van-button>
    <van-button plain class="reset-btn" @click="onReset">Reset</van-button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { showToast } from 'vant'
import { getComments, modifyComments, clearComments } from '@/api/comment'
import { approvalsTab } from '@/utils/config'

const props = defineProps<{ detailData: any }>()

const route = useRoute()
const loading = ref(false)
const commentData = ref<any>(null)
const dcComment = ref('')

const processDefinitionKey = computed(() => props.detailData?.processDefinitionKey || '')
const isDCFlow = computed(() => {
  const dcKeys = approvalsTab.find((t) => t.key === 'D&C')?.processDefinitionKeys || []
  return dcKeys.includes(processDefinitionKey.value)
})

const getCommentTypeParam = () => {
  const p = props.detailData?.processDefinitionKey
  const t = props.detailData?.taskDefinitionKey
  return p === 'nike-mpp-catalyze-non-key-door-approval' && t === 'ro-approve' ? 'c' : 'undefined'
}

// modify前检查id是否存在，避免出现数据库存在id时给后端传空id，导致数据库出现两条数据的问题
const modifyCommentsEnsuringId = async (payload: any) => {
  const hasId = Boolean(String(payload?.id || ''))
  if (!hasId) {
    const doorCode = route.query.clcDoorCode as string
    const batchId = route.query.batchId as string
    const taskId = route.query.taskId as string
    if (doorCode && batchId && taskId) {
      try {
        const res = await getComments(doorCode, batchId, taskId, getCommentTypeParam())
        const newId = String(res.data?.id || '')
        if (newId) {
          payload = { ...payload, id: newId }
        }
      } catch {}
    }
  }
  return modifyComments(payload)
}

// 根据 label 对 refComments 进行过滤和分组
const groupedRefComments = computed<Record<string, any[]>>(() => {
  const list = commentData.value?.refComments ?? []
  const valid = list.filter((it: any) => it?.typeName !== 'Invalid')
  const groups: Record<string, any[]> = {}
  for (const it of valid) {
    const label = it?.label ?? 'Other'
    ;(groups[label] ||= []).push(it)
  }
  const cg = groups['Current Gate'] || []
  if (cg.length > 0) {
    // 只展示N8.2的选项，虽然不知道为什么，但老项目是这么做的
    const filtered = cg.filter((i: any) => String(i?.typeName || '').startsWith('N8.2'))
    // 没有N8.2就展示第一条
    groups['Current Gate'] = filtered.length > 0 ? filtered : [cg[0]]
  }
  return groups
})

const groupLabels = computed(() => Object.keys(groupedRefComments.value))

// 选择结果：按 label 存储该组被勾选的选项 name 列表
const checked = ref<Record<string, string[]>>({})
const currentGate = ref('')
// 其它原因输入：按 label 存储文本
const otherReasonsText = ref<Record<string, string>>({})
const showReset = ref(false)

const getItemName = (it: any, lbl: string) => String(it?.id ?? `${lbl}-${it?.typeName}`)

const isOtherReasons = (s: any) =>
  String(s ?? '')
    .trim()
    .toLowerCase() === 'other reasons'

const onSave = async () => {
  if (isDCFlow.value) {
    const text = dcComment.value.trim()
    if (!text) {
      showToast('Please input comment')
      return
    }
    const payload = {
      batchId: route.query.batchId,
      taskId: route.query.taskId,
      doorCode: route.query.clcDoorCode,
      id: commentData.value?.id || '',
      commentTypeId: '',
      comment: text,
    }
    try {
      await modifyCommentsEnsuringId(payload)
      await fetchComments()
      showToast('Saved successfully')
    } catch (error) {
      console.error(error)
    }
    return
  }

  const allSelected = Object.values(checked.value).flat()
  if (allSelected.length === 0) {
    showToast('Please select at least one option')
    return
  }

  if (groupLabels.value.includes('Current Gate')) {
    if (!currentGate.value) {
      showToast('Please select Current Gate')
      return
    }
  }

  const list = commentData.value?.refComments || []
  const selectedIds: string[] = []
  const comments: string[] = []
  let otherReasonMissing = false

  for (const item of list) {
    const label = item.label || 'Other'
    const name = getItemName(item, label)
    const groupChecked =
      label === 'Current Gate'
        ? currentGate.value
          ? [currentGate.value]
          : []
        : checked.value[label] || []

    if (groupChecked.includes(name)) {
      if (item.id) {
        selectedIds.push(String(item.id))
      }
      if (isOtherReasons(item.typeName)) {
        const text = otherReasonsText.value[label] || ''
        if (!text.trim()) {
          otherReasonMissing = true
        }
        comments.push(text)
      }
    }
  }

  if (otherReasonMissing) {
    showToast('Please input other reasons')
    return
  }

  const payload = {
    batchId: route.query.batchId,
    taskId: route.query.taskId,
    doorCode: route.query.clcDoorCode,
    id: commentData.value?.id || '',
    commentTypeId: selectedIds.join(','),
    comment: comments.join('; '),
  }

  try {
    await modifyCommentsEnsuringId(payload)
    await fetchComments()
    showToast('Saved successfully')
  } catch (error) {
    console.error(error)
  }
}

const onReset = async () => {
  const payload = {
    batchId: route.query.batchId,
    taskId: route.query.taskId,
    doorCode: route.query.clcDoorCode,
    id: commentData.value?.id || '',
    commentTypeId: commentData.value?.commentTypeId,
    comment: commentData.value?.comment,
  }
  if (!payload.doorCode || !payload.batchId || !payload.taskId) return
  try {
    await clearComments(payload)
    if (isDCFlow.value) {
      dcComment.value = ''
    } else {
      checked.value = {}
      otherReasonsText.value = {}
      currentGate.value = ''
    }
    showToast('Reset successfully')
    await fetchComments()
  } catch (error) {
    console.error(error)
  }
}

const fetchComments = async () => {
  const doorCode = route.query.clcDoorCode as string
  const batchId = route.query.batchId as string
  const taskId = route.query.taskId as string

  if (doorCode && batchId && taskId) {
    loading.value = true
    try {
      const processDefinitionKey = props.detailData?.processDefinitionKey
      const taskDefinitionKey = props.detailData?.taskDefinitionKey

      const type =
        processDefinitionKey === 'nike-mpp-catalyze-non-key-door-approval' &&
        taskDefinitionKey === 'ro-approve'
          ? 'c'
          : 'undefined'
      const res = await getComments(doorCode, batchId, taskId, type)
      commentData.value = res.data

      // Populate form
      const savedIds = (res.data?.commentTypeId || '').split(',').filter(Boolean)
      const savedComment = res.data?.comment || ''
      if (isDCFlow.value) {
        dcComment.value = savedComment
        return
      }

      if (savedIds.length > 0) {
        const newChecked: Record<string, string[]> = {}
        const newOtherText: Record<string, string> = {}
        const list = res.data?.refComments || []

        for (const item of list) {
          if (savedIds.includes(String(item.id))) {
            const label = item.label || 'Other'
            if (!newChecked[label]) newChecked[label] = []
            newChecked[label].push(getItemName(item, label))

            if (item.typeName === 'Other reasons') {
              newOtherText[label] = savedComment
            }
          }
        }
        // Current Gate 单选
        const cgList = list.filter((i: any) => (i.label || 'Other') === 'Current Gate')
        let cgValue = ''
        for (const it of cgList) {
          if (savedIds.includes(String(it.id))) {
            cgValue = getItemName(it, 'Current Gate')
            break
          }
        }
        currentGate.value = cgValue
        checked.value = newChecked
        otherReasonsText.value = newOtherText
        const onlyOtherSelected =
          savedIds.length > 0 &&
          savedIds.every((id: any) => {
            const it = list.find((i: any) => String(i.id) === String(id))
            return it ? isOtherReasons(it.typeName) : false
          })
        showReset.value = Boolean(savedComment && savedComment.trim()) || onlyOtherSelected
      } else {
        // Reset if no saved data (important for re-entry)
        checked.value = {}
        otherReasonsText.value = {}
        currentGate.value = ''
        showReset.value = false
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
    }
  }
}

onMounted(() => {
  fetchComments()
})

const ensureOtherSelectionByText = () => {
  const groups = groupedRefComments.value
  for (const lbl of Object.keys(groups)) {
    if (lbl === 'Current Gate') continue
    const arr = groups[lbl] || []
    const otherItem = arr.find((i: any) => isOtherReasons(i?.typeName))
    if (!otherItem) continue
    const otherName = getItemName(otherItem, lbl)
    const sel = checked.value[lbl] || []
    const hasOther = sel.includes(otherName)
    const text = String(otherReasonsText.value[lbl] || '').trim()
    if (text && !hasOther) {
      checked.value[lbl] = [...sel, otherName]
    } else if (!text && hasOther) {
      checked.value[lbl] = sel.filter((n) => n !== otherName)
    }
  }
}

watch(otherReasonsText, ensureOtherSelectionByText, { deep: true })
watch(groupedRefComments, ensureOtherSelectionByText)

const saveComments = async () => {
  if (isDCFlow.value) {
    const payload = {
      batchId: route.query.batchId,
      taskId: route.query.taskId,
      doorCode: route.query.clcDoorCode,
      id: commentData.value?.id || '',
      commentTypeId: '',
      comment: (dcComment.value || '').trim(),
    }
    if (!payload.doorCode || !payload.batchId || !payload.taskId) return
    if (!payload.commentTypeId && !payload.comment) return
    try {
      await modifyCommentsEnsuringId(payload)
      await fetchComments()
    } catch (error) {}
    return
  }
  const list = commentData.value?.refComments || []
  const selectedIds: string[] = []
  const comments: string[] = []
  for (const item of list) {
    const label = item.label || 'Other'
    const name = getItemName(item, label)
    const groupChecked =
      label === 'Current Gate'
        ? currentGate.value
          ? [currentGate.value]
          : []
        : checked.value[label] || []
    if (groupChecked.includes(name) && item.id) {
      if (isOtherReasons(item.typeName)) {
        const text = (otherReasonsText.value[label] || '').trim()
        if (text) {
          selectedIds.push(String(item.id))
          comments.push(text)
        }
      } else {
        selectedIds.push(String(item.id))
      }
    }
  }
  const payload = {
    batchId: route.query.batchId,
    taskId: route.query.taskId,
    doorCode: route.query.clcDoorCode,
    id: commentData.value?.id || '',
    commentTypeId: selectedIds.join(','),
    comment: comments.join('; '),
  }
  if (!payload.doorCode || !payload.batchId || !payload.taskId) return
  if (!payload.commentTypeId && !String(payload.comment || '').trim()) return
  try {
    await modifyCommentsEnsuringId(payload)
    await fetchComments()
  } catch (error) {}
}

defineExpose({ fetchComments, saveComments })
</script>

<style scoped lang="scss">
.comment-container {
  min-height: 200px;
  padding-bottom: 64px;
}

@media (orientation: landscape) {
  .comment-container,
  .fixed-footer {
    zoom: 0.5;
  }
}

.loading-wrapper {
  display: flex;
  justify-content: center;
  padding-top: 50px;
}
.comment-group {
  padding: 10px 20px;
  margin-bottom: 12px;
}
.group-title {
  font-weight: 600;
  margin-bottom: 6px;
}
.required-star {
  color: #ed4014;
  margin-right: 4px;
}
.group-list {
  display: flex;
  flex-direction: column;
}
/* 让复选框图标与第一行文字顶齐 */
.group-list :deep(.van-checkbox) {
  align-items: flex-start;
}
.group-list :deep(.van-checkbox__icon) {
  margin-top: 2px;
}
.other-input {
  margin-top: 8px;
}
.other-textarea {
  width: 100%;
  min-height: 64px;
  resize: none;
  border: 1px solid #ebedf0;
  padding: 2px 5px;
  box-sizing: border-box;
}
.mini-btn {
  height: 28px;
}
.fixed-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 55px;
  display: flex;
  gap: 12px;
  padding: 8px 16px;
  background: #fff;
  border-top: 1px solid #eee;
}
.save-btn,
.reset-btn {
  width: 100px;
  height: 30px;
}
.save-btn {
  margin-left: 10px;
}
.reset-btn {
  color: #aaa !important;
  border-color: #aaa !important;
}
</style>
